/**
 * Scenario Sequencing Utilities
 * Handles scenario order, dependencies, and access control
 */

import { SCENARIO_DATA } from '../data/scenarios';

// Define scenario order within each track
const SCENARIO_ORDER = {
  'SaaS': ['saas-1', 'saas-2', 'saas-3', 'saas-4', 'saas-5'],
  'AI': ['ai-1', 'ai-2', 'ai-3']
};

// Define scenario dependencies (which scenarios must be completed first)
const SCENARIO_DEPENDENCIES = {
  'saas-2': ['saas-1'],
  'saas-3': ['saas-2'],
  'saas-4': ['saas-3'],
  'saas-5': ['saas-4'],
  'ai-2': ['ai-1'],
  'ai-3': ['ai-2']
};

/**
 * Get the order of scenarios for a given track
 */
export function getScenarioOrder(track) {
  return SCENARIO_ORDER[track] || [];
}

/**
 * Get all scenario IDs in order
 */
export function getAllScenariosInOrder() {
  return [
    ...SCENARIO_ORDER['SaaS'],
    ...SCENARIO_ORDER['AI']
  ];
}

/**
 * Get the first scenario ID
 */
export function getFirstScenario() {
  return 'saas-1';
}

/**
 * Check if a scenario can be accessed based on completed scenarios
 */
export function canAccessScenario(scenarioId, completedScenarios = []) {
  // First scenario is always accessible
  if (scenarioId === getFirstScenario()) {
    return { accessible: true, reason: null };
  }

  // Check dependencies
  const dependencies = SCENARIO_DEPENDENCIES[scenarioId] || [];
  
  if (dependencies.length === 0) {
    // No dependencies, scenario is accessible
    return { accessible: true, reason: null };
  }

  // Check if all dependencies are completed
  const completedIds = completedScenarios.map(s => s.id || s);
  const missingDependencies = dependencies.filter(dep => !completedIds.includes(dep));

  if (missingDependencies.length > 0) {
    // Get scenario titles for missing dependencies
    const missingTitles = missingDependencies.map(id => {
      const scenario = SCENARIO_DATA[id];
      return scenario ? scenario.title : id;
    });

    return {
      accessible: false,
      reason: `Complete the following scenario(s) first: ${missingTitles.join(', ')}`,
      missingScenarios: missingDependencies
    };
  }

  return { accessible: true, reason: null };
}

/**
 * Get the next scenario after completing the current one
 */
export function getNextScenario(currentScenarioId, completedScenarios = []) {
  const scenario = SCENARIO_DATA[currentScenarioId];
  if (!scenario) return null;

  const track = scenario.track;
  const order = getScenarioOrder(track);
  const currentIndex = order.indexOf(currentScenarioId);

  if (currentIndex === -1 || currentIndex === order.length - 1) {
    // Last scenario in track, check if there's another track
    if (track === 'SaaS') {
      // Could potentially move to AI track, but for now return null
      return null;
    }
    return null;
  }

  const nextScenarioId = order[currentIndex + 1];
  return nextScenarioId;
}

/**
 * Get scenario dependencies
 */
export function getScenarioDependencies(scenarioId) {
  return SCENARIO_DEPENDENCIES[scenarioId] || [];
}

/**
 * Check if scenario is the first in its track
 */
export function isFirstScenario(scenarioId) {
  const scenario = SCENARIO_DATA[scenarioId];
  if (!scenario) return false;

  const track = scenario.track;
  const order = getScenarioOrder(track);
  return order[0] === scenarioId;
}

